import React, { useState, useEffect } from 'react';
import { UserPlus, Shield, X, AlertCircle, CheckCircle2, Key } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomSelect from '../ui/CustomSelect';
import api from '../../api';
import { useNotification } from '../../context/NotificationContext';
import { useModal } from '../../context/ModalContext';
import Tooltip from '../ui/Tooltip';

const PERMISSIONS_SCOPES = [
  { code: 'FORUM_DOSSIER_VIEW', label: 'Voir le dossier', desc: 'Peut consulter les informations générales du dossier' },
  { code: 'FORUM_DOSSIER_EDIT', label: 'Éditer le dossier', desc: 'Peut modifier les champs du dossier' },
  { code: 'FORUM_DOC_VIEW', label: 'Voir les documents', desc: 'Peut consulter les documents du dossier' },
  { code: 'FORUM_DOC_MANAGE', label: 'Gérer les documents', desc: 'Peut ajouter/supprimer des documents' },
  { code: 'FORUM_MESSAGE_CREATE', label: 'Créer des messages', desc: 'Peut poster dans l\'onglet analyse' },
  { code: 'TRANSACTION_VIEW', label: 'Voir la transaction', desc: 'Peut consulter la transaction liée' },
];

const ResourceAccess = ({ resourceType, resourceId }) => {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const { showToast } = useNotification();

  // Modal form state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('FORUM_DOSSIER_VIEW');
  const [selectedState, setSelectedState] = useState('GRANT');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchOverrides();
    fetchUsers();
  }, [resourceId]);

  const fetchOverrides = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/permissions/overrides/${resourceType}/${resourceId}`);
      setOverrides(res.data);
    } catch (e) {
      console.error(e);
      showToast("Erreur lors de la récupération des exceptions", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get("/utilisateurs");
      if (Array.isArray(res.data)) {
        setUtilisateurs(res.data);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des utilisateurs", err);
    }
  };

  const handleCreateOverride = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      showToast("Veuillez sélectionner un membre", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      await api.post('/permissions/overrides', {
        resourceType,
        resourceId,
        userId: selectedUser,
        permissionCode: selectedPermission,
        state: selectedState
      });
      showToast("Exception ajoutée avec succès", "success");
      setIsModalOpen(false);
      setSelectedUser('');
      fetchOverrides();
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de l'ajout de l'exception", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { confirm } = useModal();

  const handleDeleteOverride = async (id) => {
    const isConfirmed = await confirm({
      title: "Supprimer l'exception",
      message: "Voulez-vous vraiment supprimer cette exception ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/permissions/overrides/${id}`);
      showToast("Exception supprimée", "success");
      fetchOverrides();
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la suppression", "error");
    }
  };

  return (
    <div className="bg-surface border border-[var(--hp-color-border-muted)] shadow-hp-md rounded-2xl p-6">
      <div className="flex justify-between items-center mb-6 border-b border-[var(--hp-color-border-muted)] pb-4">
        <div>
          <h3 className="text-lg font-bold text-primary flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            Gestion des accès
          </h3>
          <p className="text-sm text-tertiary mt-1">Définissez des autorisations spécifiques à cette ressource (Override).</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--hp-info-600)] text-white font-medium rounded-xl shadow-sm hover:bg-[var(--hp-info-700)] transition"
        >
          <UserPlus className="w-4 h-4" />
          Ajouter une exception
        </button>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="p-4 text-center text-subtle">Chargement des accès...</div>
        ) : overrides.length === 0 ? (
          <div className="text-center py-8 text-subtle">
            <Shield className="w-12 h-12 mx-auto mb-3 text-[var(--hp-color-border-muted)]" />
            <p>Aucune exception n'a été configurée.</p>
            <p className="text-xs mt-1">Les permissions des rôles s'appliquent par défaut.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {overrides.map((ov) => (
              <div key={ov.id} className="workspace-card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <div className="w-8 h-8 rounded-full bg-[var(--hp-info-100)] text-[var(--hp-info-700)] flex items-center justify-center font-bold text-sm shrink-0 shadow-sm ring-2 ring-workspace">
                      {ov.memberName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-primary text-sm">{ov.memberName}</p>
                      <p className="text-xs text-subtle">{ov.memberEmail}</p>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-8 bg-workspace"></div>
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-wider text-tertiary font-semibold mb-0.5">Permission</span>
                    <span className="text-sm font-mono text-secondary">{ov.permissionCode}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t border-workspace sm:border-t-0">
                  {ov.state === 'GRANT' ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 text-success-700 px-3 py-1 text-xs font-bold border border-success-200 shadow-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      Autorisé
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-50 text-danger-700 px-3 py-1 text-xs font-bold border border-danger-200 shadow-sm">
                      <AlertCircle className="w-4 h-4" />
                      Refusé
                    </span>
                  )}
                  
                  <Tooltip content="Supprimer l'exception">
                    <button 
                      onClick={() => handleDeleteOverride(ov.id)}
                      className="text-subtle hover:text-danger-600 transition-colors p-2 rounded-lg hover:bg-danger-50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100" 
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Ajout Exception */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-0">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative bg-surface rounded-xl shadow-hp-xl border border-workspace w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col z-10"
            >
              <div className="flex justify-between items-center p-6 border-b border-workspace bg-surface-muted sticky top-0 z-20">
                <h3 className="text-xl font-bold text-primary flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-tertiary" />
                  Ajouter une exception
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-tertiary hover:text-primary transition p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateOverride} className="p-6 space-y-6">
                <div className="relative z-[60]">
                  <label className="block text-sm font-semibold text-primary mb-2">Membre de l'équipe *</label>
                  <CustomSelect 
                    value={selectedUser}
                    onChange={(val) => setSelectedUser(val)}
                    options={utilisateurs.map(u => ({ value: u.id, label: `${u.Nom} (${u.Email})` }))}
                    placeholder="Sélectionner un membre..."
                  />
                </div>
                
                <div className="relative z-50">
                  <label className="block text-sm font-semibold text-primary mb-2">Permission (Scope) *</label>
                  <CustomSelect 
                    value={selectedPermission}
                    onChange={(val) => setSelectedPermission(val)}
                    options={PERMISSIONS_SCOPES.map(p => ({ value: p.code, label: `${p.label} (${p.code})` }))}
                  />
                </div>
                
                <div className="relative z-40">
                  <label className="block text-sm font-semibold text-primary mb-3">Règle d'accès *</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setSelectedState('GRANT')}
                      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedState === 'GRANT' 
                          ? 'border-success-500 bg-success-50/50 shadow-sm' 
                          : 'border-workspace bg-surface hover:bg-surface-muted'
                      }`}
                    >
                      <CheckCircle2 className={`w-8 h-8 ${selectedState === 'GRANT' ? 'text-success-600' : 'text-subtle'}`} strokeWidth={2.5} />
                      <span className={`text-sm font-bold ${selectedState === 'GRANT' ? 'text-success-700' : 'text-primary'}`}>Accorder (GRANT)</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setSelectedState('DENY')}
                      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedState === 'DENY' 
                          ? 'border-danger-500 bg-danger-50/50 shadow-sm' 
                          : 'border-workspace bg-surface hover:bg-surface-muted'
                      }`}
                    >
                      <AlertCircle className={`w-8 h-8 ${selectedState === 'DENY' ? 'text-danger-600' : 'text-subtle'}`} strokeWidth={2.5} />
                      <span className={`text-sm font-bold ${selectedState === 'DENY' ? 'text-danger-700' : 'text-primary'}`}>Refuser (DENY)</span>
                    </button>
                  </div>
                </div>
                
                <div className="pt-6 flex justify-end gap-3 border-t border-workspace mt-6 relative z-10">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">Annuler</button>
                  <button type="submit" disabled={isSubmitting || !selectedUser || !selectedPermission || !selectedState} className="btn btn-primary bg-[var(--hp-info-600)] hover:bg-[var(--hp-info-700)] text-white border-transparent">
                    {isSubmitting ? 'Enregistrement...' : 'Confirmer l\'exception'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResourceAccess;
